import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Paper,
} from '@mui/material';
import axios from 'axios';

function GeoLocation() {
  const [ip, setIp] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleLookup = async () => {
    if (!ip) {
      setError('Please enter an IP address');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Use free IP geolocation API
      const response = await axios.get(`https://ipapi.co/${ip}/json/`);
      
      if (response.data.error) {
        setError('Invalid IP address or IP not found');
        return;
      }
      
      setResult({
        ip: ip,
        country: response.data.country_name,
        region: response.data.region,
        city: response.data.city,
        ll: [response.data.latitude, response.data.longitude]
      });
    } catch (err) {
      setError('Failed to get location information for this IP');
    } finally {
      setLoading(false);
    }
  };

  const formatResult = () => {
    if (!result) return null;

    const { country, region, city, ll } = result;
    const [latitude, longitude] = ll || [];

    return (
      <Card sx={{ 
        mt: 2, 
        p: 3, 
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'success.main',
        borderRadius: 2
      }}>
        <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
          Geolocation Results
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ 
              p: 2, 
              bgcolor: 'background.default',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Location Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body1">
                  <strong>Country:</strong> {country}
                </Typography>
                <Typography variant="body1">
                  <strong>Region:</strong> {region}
                </Typography>
                <Typography variant="body1">
                  <strong>City:</strong> {city}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ 
              p: 2, 
              bgcolor: 'background.default',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Coordinates
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  <strong>Latitude:</strong> {latitude}
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  <strong>Longitude:</strong> {longitude}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Card>
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Geo Location
      </Typography>
      <Card sx={{ 
        p: 0,
        borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(220, 20, 60, 0.05) 0%, rgba(0, 0, 0, 0.02) 100%)',
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="IP Address"
              variant="outlined"
              value={ip}
              onChange={(e) => setIp(e.target.value)}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleLookup}
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Lookup Location'}
            </Button>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {formatResult()}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default GeoLocation;
